/// <reference types="@types/applepayjs" />
import type { PaymentRequest, PaymentRequestPaymentMethodEvent, Stripe, StripeConstructor } from "@stripe/stripe-js";

// Stripe.js must be loaded via script tag for PCI compliance
declare global {
  interface Window {
    Stripe?: StripeConstructor;
    ApplePaySession?: typeof ApplePaySession;
  }
}

export interface ApplePayPaymentRequestConfig {
  country: string;
  currency: string;
  total: { label: string; amount: number }; // Amount in cents (atomic units)
  supportedNetworks?: string[];
  merchantCapabilities?: string[];
}

export type ShowApplePaySheetResult =
  | {
      success: true;
      paymentMethodId: string;
      paymentMethodEvent: PaymentRequestPaymentMethodEvent;
      billingDetails?: {
        email?: string;
        name?: string;
        address?: {
          city?: string | null;
          country?: string | null;
          line1?: string | null;
          line2?: string | null;
          postal_code?: string | null;
          state?: string | null;
        } | null;
      };
    }
  | { success: false; cancelled: true }
  | { success: false; error: string };

export type ConfirmResult = { success: true } | { success: false; error: string };

export enum ApplePayMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

/**
 * Stripe Apple Pay Adapter using Stripe's PaymentRequest API.
 *
 * This uses Stripe's PaymentRequest API instead of the native ApplePaySession API.
 * The key benefit is that Stripe handles merchant validation automatically,
 * which is required for Apple Pay to work correctly.
 *
 * IMPORTANT: To comply with browser security requirements, Apple Pay must be shown
 * synchronously from a user click. The flow is:
 *
 * 1. Call preparePaymentRequest() BEFORE user clicks (e.g., when page loads)
 *    - This creates the PaymentRequest and calls canMakePayment()
 * 2. On user click, call showPreparedPaymentSheet()
 *    - This calls show() on the pre-created PaymentRequest (no async!)
 * 3. Handle the paymentmethod event and call complete()
 */
export class StripeApplePayAdapter {
  private stripe: Stripe | null = null;
  private mockScenario: ApplePayMockScenario;
  private preparedPaymentRequest: PaymentRequest | null = null;

  constructor(mockScenario?: ApplePayMockScenario) {
    this.mockScenario = mockScenario ?? ApplePayMockScenario.None;
  }

  initialize(publishableKey: string): boolean {
    switch (this.mockScenario) {
      case ApplePayMockScenario.None: {
        console.log("[ApplePay] initialize called");
        if (!window.Stripe) {
          console.log("[ApplePay] Stripe.js not loaded");
          return false;
        }
        this.stripe = window.Stripe(publishableKey);
        const success = this.stripe !== null;
        console.log("[ApplePay] initialize result:", success);
        return success;
      }
      case ApplePayMockScenario.Success:
      case ApplePayMockScenario.Cancelled: {
        console.log("[MockApplePay] initialize called");
        console.log("[MockApplePay] initialize result:", true);
        return true;
      }
    }
  }

  /**
   * Check if Apple Pay is available on this device/browser.
   * Uses Stripe's PaymentRequest.canMakePayment() which is more reliable
   * than checking ApplePaySession directly.
   */
  static isAvailable(): boolean {
    // Basic check - ApplePaySession must exist (Safari on iOS/macOS)
    if (typeof window === "undefined" || !window.ApplePaySession) {
      console.log("[ApplePay] ApplePaySession not available");
      return false;
    }

    // Check if device supports Apple Pay
    const canMakePayments = window.ApplePaySession.canMakePayments();
    console.log("[ApplePay] canMakePayments:", canMakePayments);
    return canMakePayments;
  }

  /**
   * Check availability using Stripe's PaymentRequest API.
   * This is more accurate as it checks Stripe's configuration too.
   */
  async canMakePayment(): Promise<{ applePay: boolean; googlePay: boolean } | null> {
    if (!this.stripe) {
      console.log("[ApplePay] Stripe not initialized");
      return null;
    }

    // Create a temporary payment request to check availability
    const pr = this.stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: "Check", amount: 100 },
    });

    const result = await pr.canMakePayment();
    console.log("[ApplePay] canMakePayment result:", result);

    if (!result) {
      return null;
    }

    // Map Stripe's result to our expected format
    return {
      applePay: result.applePay ?? false,
      googlePay: result.googlePay ?? false,
    };
  }

  /**
   * Prepare a PaymentRequest for later use. Call this BEFORE the user clicks.
   * This creates the PaymentRequest and calls canMakePayment() to validate.
   *
   * @returns Promise with success status and whether Apple Pay is available
   */
  async preparePaymentRequest(config: ApplePayPaymentRequestConfig): Promise<{
    success: boolean;
    applePay?: boolean;
    googlePay?: boolean;
    error?: string;
  }> {
    if (this.mockScenario !== ApplePayMockScenario.None) {
      console.log("[MockApplePay] preparePaymentRequest called");
      this.preparedConfig = config;
      return { success: true, applePay: true, googlePay: false };
    }

    console.log("[ApplePay] preparePaymentRequest called", config);

    if (!this.stripe) {
      return { success: false, error: "Stripe not initialized" };
    }

    // Create the PaymentRequest
    const paymentRequest = this.stripe.paymentRequest({
      country: config.country,
      currency: config.currency.toLowerCase(),
      total: {
        label: config.total.label,
        amount: config.total.amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check availability - this MUST be called before show()
    const canMakePaymentResult = await paymentRequest.canMakePayment();
    console.log("[ApplePay] preparePaymentRequest canMakePayment:", canMakePaymentResult);

    if (!canMakePaymentResult) {
      return {
        success: false,
        error: "Payment methods not available on this device/browser",
        applePay: false,
        googlePay: false,
      };
    }

    // Store the prepared PaymentRequest for later use
    this.preparedPaymentRequest = paymentRequest;
    this.preparedConfig = config;

    return {
      success: true,
      applePay: canMakePaymentResult.applePay ?? false,
      googlePay: canMakePaymentResult.googlePay ?? false,
    };
  }

  /**
   * Check if a PaymentRequest has been prepared and is ready to show.
   */
  isPrepared(): boolean {
    return this.preparedPaymentRequest !== null || this.mockScenario !== ApplePayMockScenario.None;
  }

  /**
   * Clear the prepared PaymentRequest.
   */
  clearPrepared(): void {
    this.preparedPaymentRequest = null;
    this.preparedConfig = null;
  }

  /**
   * Show the prepared Apple Pay payment sheet.
   * MUST be called synchronously from a user click handler.
   * Call preparePaymentRequest() before the click to set up the PaymentRequest.
   */
  showPreparedPaymentSheet(): Promise<ShowApplePaySheetResult> {
    switch (this.mockScenario) {
      case ApplePayMockScenario.None: {
        console.log("[ApplePay] showPreparedPaymentSheet called");

        if (!this.preparedPaymentRequest) {
          console.error("[ApplePay] No prepared PaymentRequest! Call preparePaymentRequest() first.");
          return Promise.resolve({
            success: false,
            error: "PaymentRequest not prepared. Call preparePaymentRequest() before showing the sheet.",
          });
        }

        const paymentRequest = this.preparedPaymentRequest;

        // Return a promise that resolves when the user completes or cancels
        return new Promise((resolve) => {
          // Handle successful payment method creation
          paymentRequest.on("paymentmethod", (evt: PaymentRequestPaymentMethodEvent) => {
            console.log("[ApplePay] paymentmethod event received", {
              paymentMethodId: evt.paymentMethod.id,
              payerEmail: evt.payerEmail,
              payerName: evt.payerName,
            });

            // Clear the prepared request after use
            this.preparedPaymentRequest = null;

            resolve({
              success: true,
              paymentMethodId: evt.paymentMethod.id,
              paymentMethodEvent: evt,
              billingDetails: {
                email: evt.payerEmail ?? undefined,
                name: evt.payerName ?? undefined,
                address: evt.paymentMethod.billing_details?.address ?? undefined,
              },
            });
          });

          // Handle cancellation
          paymentRequest.on("cancel", () => {
            console.log("[ApplePay] cancel event - user cancelled");
            // Clear the prepared request after cancellation
            this.preparedPaymentRequest = null;
            resolve({ success: false, cancelled: true });
          });

          // Show the payment sheet immediately (no async operations before this!)
          console.log("[ApplePay] showing payment sheet");
          paymentRequest.show();
        });
      }

      case ApplePayMockScenario.Success: {
        console.log("[MockApplePay] showPreparedPaymentSheet: success");
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              success: true,
              paymentMethodId: "pm_mock_apple_pay_test",
              paymentMethodEvent: {
                complete: (status: string) => console.log(`[MockApplePay] complete: ${status}`),
                paymentMethod: {
                  id: "pm_mock_apple_pay_test",
                  object: "payment_method",
                  type: "card",
                  card: {
                    brand: "visa",
                    last4: "4242",
                    exp_month: 12,
                    exp_year: 2030,
                  },
                  billing_details: {
                    email: "test@example.com",
                    name: "Test User",
                  },
                },
                payerEmail: "test@example.com",
                payerName: "Test User",
              } as unknown as PaymentRequestPaymentMethodEvent,
              billingDetails: {
                email: "test@example.com",
                name: "Test User",
              },
            });
          }, 500);
        });
      }

      case ApplePayMockScenario.Cancelled: {
        console.log("[MockApplePay] showPreparedPaymentSheet: cancelled");
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({ success: false, cancelled: true });
          }, 500);
        });
      }
    }
  }

  /**
   * @deprecated Use preparePaymentRequest() + showPreparedPaymentSheet() instead.
   * This method creates a new PaymentRequest and shows it, which doesn't work
   * with browser security requirements (show() must be called synchronously).
   */
  async showPaymentSheet(config: ApplePayPaymentRequestConfig): Promise<ShowApplePaySheetResult> {
    console.warn(
      "[ApplePay] showPaymentSheet is deprecated. Use preparePaymentRequest() + showPreparedPaymentSheet() instead.",
    );

    // Try to prepare and show in one go (will likely fail due to user gesture requirement)
    const prepareResult = await this.preparePaymentRequest(config);
    if (!prepareResult.success) {
      return { success: false, error: prepareResult.error ?? "Failed to prepare payment request" };
    }

    return this.showPreparedPaymentSheet();
  }

  /**
   * Complete the payment flow in the Apple Pay sheet.
   * Call this after confirming the payment on the server.
   *
   * @param evt - The PaymentRequestPaymentMethodEvent from showPaymentSheet
   * @param status - 'success' or 'fail'
   */
  completePayment(evt: PaymentRequestPaymentMethodEvent, status: "success" | "fail"): void {
    console.log("[ApplePay] completePayment:", status);
    evt.complete(status);
  }

  /**
   * Confirm PaymentIntent with the PaymentMethod.
   * This is called after showPaymentSheet to finalize the payment.
   *
   * @deprecated - Server-side confirmation is preferred. The backend should
   * call stripe.PaymentIntent.confirm() with the payment_method_id.
   */
  async confirmPaymentIntent(clientSecret: string, paymentMethodId: string): Promise<ConfirmResult> {
    switch (this.mockScenario) {
      case ApplePayMockScenario.None: {
        console.log("[ApplePay] confirmPaymentIntent called", {
          clientSecret: `${clientSecret.slice(0, 20)}...`,
          paymentMethodId,
        });
        if (!this.stripe) {
          console.log("[ApplePay] confirmPaymentIntent: Stripe not initialized");
          return { success: false, error: "Stripe not initialized" };
        }

        const { error } = await this.stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId,
        });

        if (error) {
          console.log("[ApplePay] confirmPaymentIntent error:", error);
          return { success: false, error: error.message ?? "Payment failed" };
        }

        console.log("[ApplePay] confirmPaymentIntent success");
        return { success: true };
      }
      case ApplePayMockScenario.Success: {
        console.log("[MockApplePay] confirmPaymentIntent called", { clientSecret, paymentMethodId });
        return { success: true };
      }
      case ApplePayMockScenario.Cancelled: {
        throw new Error("confirmPaymentIntent should not be called when scenario is Cancelled");
      }
    }
  }
}
